import {
  buildTagSections,
  isInstanceSelected,
  parseKey,
  reconcile,
  STEP_FREE_ALTERNATIVE_IDS,
  tagIdsOf,
  type SelectionKey,
} from "common/utils/accessibilitySelection";

interface SelectionState {
  accessibilityOptions: number[];
  accessibilitySelections?: SelectionKey[];
}

/**
 * Writes a selection to a filter-state atom: the keys, plus the plain tag ids
 * every existing reader (badges, What's-nearby, native) still uses.
 */
export function withSelection<S extends SelectionState>(state: S, keys: SelectionKey[]): S {
  return {
    ...state,
    accessibilitySelections: keys,
    accessibilityOptions: tagIdsOf(keys),
  };
}

/**
 * The selection a filter state holds. Reconciled per tag with the id list, so a
 * writer that only knows tag ids (What's-nearby) can neither leave a stale
 * section behind nor widen a retained one.
 */
export function selectionOf(state: SelectionState | null | undefined): SelectionKey[] {
  if (!state) return [];
  return reconcile(state.accessibilitySelections ?? [], state.accessibilityOptions ?? []);
}

export function sameSelection(a: SelectionKey[], b: SelectionKey[]): boolean {
  return a.length === b.length && a.every((key, i) => key === b[i]);
}

/**
 * Ticked boxes in one section, over all its tags so a search box or "show more" can't hide a tick.
 *
 * @param keys The current selection.
 * @param sectionId The section counted.
 * @param tags Every tag the section lists.
 * @param alternatives The step-free alternatives, drawn (and so counted) only under a ticked basic step-free box.
 * @param isComplex A complex section, whose alternatives are ordinary tags.
 * @returns The number of ticked boxes.
 */
export function sectionSelectedCount(
  keys: SelectionKey[],
  sectionId: number,
  tags: { id: number }[],
  alternatives: { id: number }[],
  isComplex: boolean,
): number {
  const ticked = tags.filter((tag) => isInstanceSelected(keys, sectionId, tag.id));
  if (isComplex || !ticked.some((tag) => tag.id === 428)) return ticked.length;
  return ticked.length + alternatives.filter((tag) => isInstanceSelected(keys, sectionId, tag.id)).length;
}

interface CountedSection {
  id: number;
  kind?: "basic" | "complex";
  accessibility_tags: { id: number }[];
}

/**
 * The count a badge shows for a selection: the panel's ticked boxes summed over its sections, plus one
 * per key no section draws, so an applied filter the panel cannot show is never hidden.
 *
 * @param keys The current selection.
 * @param sections The panel's sections, with the step-free alternatives split out.
 * @param alternatives The split-out step-free alternatives.
 * @returns The badge count; `keys.length` while no sections are loaded.
 */
export function selectionDisplayCount(
  keys: SelectionKey[],
  sections: CountedSection[],
  alternatives: { id: number }[],
): number {
  const ticked = sections.reduce(
    (sum, section) =>
      sum + sectionSelectedCount(keys, section.id, section.accessibility_tags, alternatives, section.kind === "complex"),
    0,
  );
  const alternativeIds = alternatives.map((tag) => tag.id);
  const draws = (section: CountedSection, tagId: number) =>
    section.accessibility_tags.some((tag) => tag.id === tagId) ||
    (alternativeIds.includes(tagId) &&
      section.kind !== "complex" &&
      section.accessibility_tags.some((tag) => tag.id === 428) &&
      isInstanceSelected(keys, section.id, 428));
  const undrawn = keys.filter((key) => {
    const parsed = parseKey(key);
    return (
      parsed !== null &&
      !sections.some((section) => (parsed.headingId === null || parsed.headingId === section.id) && draws(section, parsed.tagId))
    );
  });
  return ticked + undrawn.length;
}

export interface FilterSectionTag {
  id: number;
  name: string;
}

export interface FilterSectionLike {
  id: number;
  name: string;
  kind?: "basic" | "complex";
  accessibility_tags: FilterSectionTag[];
}

/** Splits the step-free alternatives out of the basic sections, once each; complex sections keep theirs. */
export function splitStepFreeAlternatives<S extends FilterSectionLike>(
  sections: S[],
): { preparedData: S[]; tagsToAdd: FilterSectionTag[] } {
  const tagsToAdd: FilterSectionTag[] = [];
  const split: number[] = [];

  sections.forEach((section) => {
    if (section.kind === "complex") return;
    section.accessibility_tags?.forEach((tag) => {
      if (STEP_FREE_ALTERNATIVE_IDS.includes(tag.id) && !split.includes(tag.id)) {
        tagsToAdd.push(tag);
        split.push(tag.id);
      }
    });
  });

  const preparedData = sections.map((section) =>
    section.kind === "complex"
      ? section
      : { ...section, accessibility_tags: section.accessibility_tags?.filter((tag) => !split.includes(tag.id)) || [] },
  );

  return { preparedData, tagsToAdd };
}

/** A section panel's list with the step-free alternatives split out, and the maps its ticks spread over. */
export function sectionPanel<S extends FilterSectionLike>(sections: S[]) {
  const { preparedData, tagsToAdd } = splitStepFreeAlternatives(sections);
  return {
    preparedData,
    tagsToAdd,
    // Ticks spread over the basic sections only, as does what an untick leaves of a section-less key.
    tagSections: buildTagSections(preparedData.filter((section) => section.kind !== "complex")),
    allSections: buildTagSections(preparedData),
    complexSectionIds: preparedData.filter((section) => section.kind === "complex").map((section) => section.id),
  };
}

/**
 * The search's own sections when they can replace the full list, else undefined.
 * Only /search has a result set; elsewhere the pins data can be a stale search's. An empty list
 * (nothing recorded, or only private-template venues) falls back to the full list.
 */
export function searchPageSections<S>(pathname: string, sections: S[] | undefined): S[] | undefined {
  return pathname.startsWith("/search") && sections !== undefined && sections.length > 0 ? sections : undefined;
}
